import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, Container, Paper, Skeleton, Stack, Typography } from '@mui/material';
import { applicationsApi, mailApi } from '../../services/api';
import { useToast } from '../../hooks/useToast';
import { ApplicationListItem, EmailTrackingMode, EmailTrackingSettings, EmailTrackingSetup, InboundMessage, InboundMessageList, MailClassification } from '../../types';
import { errorMessage } from '../../utils/errors';
import CopyField from '../../components/mail/CopyField';
import CopySetup from '../../components/mail/CopySetup';
import MessageList from '../../components/mail/MessageList';
import ModeChooser from '../../components/mail/ModeChooser';

const MODE_NOTES: Record<EmailTrackingMode, string> = {
  off: 'Tracking is off. Choose how employer mail should reach JobFlo to switch it on.',
  alias: 'The extension fills your JobFlo address into application forms. Mail sent to it is read for status and forwarded to you.',
  copy: 'You apply with your own email. A rule in your mailbox sends JobFlo a copy of mail from hiring systems.',
};

/**
 * Application status tracking by email: choosing how employer mail reaches
 * JobFlo, the one-time setup of the copy mode, and the mail that could not be
 * placed on an application.
 */
const EmailTrackingPage: React.FC = () => {
  const { show, toast } = useToast();
  const [settings, setSettings] = useState<EmailTrackingSettings | null>(null);
  const [setup, setSetup] = useState<EmailTrackingSetup | null>(null);
  const [mail, setMail] = useState<InboundMessageList | null>(null);
  const [applications, setApplications] = useState<ApplicationListItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);

  const loadMail = useCallback(async () => {
    const [setupData, messages] = await Promise.all([mailApi.setup(), mailApi.messages()]);
    setSetup(setupData);
    setMail(messages);
  }, []);

  const load = useCallback(async () => {
    try {
      const [settingsData, apps] = await Promise.all([mailApi.settings(), applicationsApi.list()]);
      setSettings(settingsData);
      setApplications(apps.filter((application) => application.status !== 'saved'));
      await loadMail();
      setError(null);
    } catch (e) {
      setError(errorMessage(e, 'Could not load email tracking. Please try again.'));
    }
  }, [loadMail]);

  useEffect(() => {
    load();
  }, [load]);

  const choose = async (mode: EmailTrackingMode) => {
    setSaving(true);
    try {
      setSettings(await mailApi.updateSettings({ mode }));
      await loadMail();
      show(mode === 'off' ? 'Email tracking turned off.' : mode === 'alias' ? 'Applications will now use your JobFlo address.' : 'Copy mode is on. Set up the forwarding rule below.');
    } catch (e) {
      show(errorMessage(e, 'Could not change the setting.'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const check = async () => {
    setChecking(true);
    try {
      await loadMail();
    } catch (e) {
      show(errorMessage(e, 'Could not check. Please try again.'), 'error');
    } finally {
      setChecking(false);
    }
  };

  const changeMessage = async (message: InboundMessage, change: { application?: string | null; classification?: MailClassification }) => {
    try {
      await mailApi.updateMessage(message.id, change);
      await loadMail();
      show('Saved.');
    } catch (e) {
      show(errorMessage(e, 'Could not save the change.'), 'error');
    }
  };

  const unmatched = mail?.results.filter((message) => !message.application && message.classification !== 'verification') ?? [];
  const matched = mail?.results.filter((message) => message.application || message.classification === 'verification') ?? [];

  return (
    <Container maxWidth="lg" component="main" sx={{ py: { xs: 2, md: 3 } }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
          Email tracking
        </Typography>
        <Typography variant="body2" color="text.secondary">
          JobFlo reads the emails employers send about your applications and keeps each application's status up to date:
          received, interview, assessment, offer or rejected.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={load}>Retry</Button>}>
          {error}
        </Alert>
      )}

      {!settings ? (
        !error && (
          <Stack spacing={1.5} aria-busy="true" aria-label="Loading email tracking">
            <Skeleton variant="rounded" height={90} />
            <Skeleton variant="rounded" height={320} />
          </Stack>
        )
      ) : (
        <Stack spacing={2}>
          <Paper variant="outlined" component="section" aria-labelledby="tracking-address-title" sx={{ p: 2 }}>
            <Typography id="tracking-address-title" variant="h6" component="h2" sx={{ fontWeight: 700 }}>
              Your JobFlo address
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {MODE_NOTES[settings.mode]}
            </Typography>
            <Box sx={{ maxWidth: 420 }}>
              <CopyField label="Your JobFlo address" value={settings.address} />
            </Box>
          </Paper>

          <Box component="section" aria-labelledby="tracking-mode-title">
            <Typography id="tracking-mode-title" variant="h6" component="h2" sx={{ fontWeight: 700, mb: 1 }}>
              How employer mail reaches JobFlo
            </Typography>
            {settings.plan_allows && settings.mode === 'off' ? (
              <Alert
                severity="info"
                action={
                  <Button color="inherit" size="small" component={RouterLink} to="/plan">
                    See plans
                  </Button>
                }
              >
                {settings.plan_allows}
              </Alert>
            ) : (
              <ModeChooser settings={settings} saving={saving} onChoose={choose} />
            )}
            {settings.plan_allows && settings.mode !== 'off' && (
              <Alert severity="warning" sx={{ mt: 1.5 }} action={<Button color="inherit" size="small" component={RouterLink} to="/plan">See plans</Button>}>
                {settings.plan_allows} Mail to your JobFlo address still reaches you; it is no longer read for status.
              </Alert>
            )}
          </Box>

          {settings.mode === 'copy' && setup && <CopySetup setup={setup} onCheck={check} checking={checking} />}

          {settings.mode === 'alias' && (
            <Paper variant="outlined" component="section" aria-labelledby="tracking-spam-title" sx={{ p: 2 }}>
              <Typography id="tracking-spam-title" variant="h6" component="h2" sx={{ fontWeight: 700 }}>
                Keep forwarded mail out of spam
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Employer mail reaches {settings.forwards_to} from JobFlo's address. Mailboxes sometimes distrust a new
                sender, so tell yours once: in Gmail create a filter with this in the "From" box and tick "Never send it
                to Spam"; in Outlook add it to "Safe senders".
              </Typography>
              <Box sx={{ maxWidth: 420 }}>
                <CopyField label="Filter: From" value={settings.address.split('@')[1] ?? ''} />
              </Box>
            </Paper>
          )}

          {settings.mode !== 'off' && mail && (
            <>
              <Paper variant="outlined" component="section" aria-labelledby="tracking-unmatched-title" sx={{ p: 2 }}>
                <Typography id="tracking-unmatched-title" variant="h6" component="h2" sx={{ fontWeight: 700 }}>
                  Mail JobFlo could not place
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  These emails did not clearly name a company you applied to. Say which application each is about and it
                  joins that application's timeline.
                </Typography>
                <MessageList messages={unmatched} applications={applications} onChange={changeMessage} empty="Nothing waiting. Every email so far was matched to an application." />
              </Paper>

              <Paper variant="outlined" component="section" aria-labelledby="tracking-recent-title" sx={{ p: 2 }}>
                <Typography id="tracking-recent-title" variant="h6" component="h2" sx={{ fontWeight: 700 }}>
                  Recent mail
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  What arrived and what JobFlo made of it. Correct anything that is wrong; your word is final.
                </Typography>
                <MessageList messages={matched} applications={applications} onChange={changeMessage} empty="No employer mail has arrived yet." />
              </Paper>
            </>
          )}
        </Stack>
      )}
      {toast}
    </Container>
  );
};

export default EmailTrackingPage;
